import * as React from 'react'
import { Link } from 'react-router-dom'
import { Popover } from '@base-ui-components/react/popover'
import styles from './index.module.css'

interface DropdownMenuRootProps {
  children: React.ReactNode
  open?: boolean
  defaultOpen?: boolean
  // eslint-disable-next-line no-unused-vars
  onOpenChange?: (open: boolean) => void
  modal?: boolean | 'trap-focus'
}

interface DropdownMenuContentProps {
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  alignOffset?: number
  className?: string
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode
  className?: string
}

type DropdownMenuItemProps =
  | {
      children: React.ReactNode
      onClick: () => void
      to?: never
      disabled?: boolean
      className?: string
      dataScreenshotsId?: string
    }
  | {
      children: React.ReactNode
      to: string
      onClick?: never
      disabled?: boolean
      className?: string
      dataScreenshotsId?: string
    }

/**
 * @see {@link https://base-ui.com/react/components/popover | Base UI Popover docs}
 */

export function DropdownMenu({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  modal = false,
}: DropdownMenuRootProps) {
  return (
    <Popover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
    >
      {children}
    </Popover.Root>
  )
}

export function DropdownMenuContent({
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  className,
}: DropdownMenuContentProps) {
  return (
    <Popover.Portal>
      <Popover.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={styles.DropdownMenuPositioner}
      >
        <Popover.Popup className={className || styles.DropdownMenu}>
          {children}
        </Popover.Popup>
      </Popover.Positioner>
    </Popover.Portal>
  )
}

export function DropdownMenuTrigger({
  children,
  className,
}: DropdownMenuTriggerProps) {
  return <Popover.Trigger className={className}>{children}</Popover.Trigger>
}

export function DropdownMenuItem({
  children,
  onClick,
  to,
  disabled = false,
  className,
  dataScreenshotsId,
}: DropdownMenuItemProps) {
  if (to) {
    return (
      <Popover.Close
        render={(props) => (
          <Link
            {...props}
            to={to}
            className={`${styles.DropdownMenuItem} ${disabled ? styles.disabled : ''} ${className || ''}`}
            aria-disabled={disabled}
            onClick={(e) => {
              if (disabled) {
                e.preventDefault()
              }
              props.onClick?.(e)
            }}
            data-screenshots-id={dataScreenshotsId}
          >
            {children}
          </Link>
        )}
      />
    )
  }

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
    }
  }

  return (
    <Popover.Close
      render={(props) => (
        <button
          data-screenshots-id={dataScreenshotsId}
          {...props}
          className={`${styles.DropdownMenuItem} ${disabled ? styles.disabled : ''} ${className || ''}`}
          onClick={(e) => {
            handleClick()
            props.onClick?.(e)
          }}
          disabled={disabled}
          type="button"
        >
          {children}
        </button>
      )}
    />
  )
}
